import { Controller } from "@hotwired/stimulus";

// Fades out and disables floating buttons during scroll to reduce visual clutter
// and prevent accidental clicks while scrolling. Buttons fade back in when scrolling stops.
//
// Usage:
//   <body data-controller="scroll-fade">
//     <div data-scroll-fade-target="fadeable">Button 1</div>
//     <div data-scroll-fade-target="fadeable">Button 2</div>
//   </body>
//
// The controller will automatically detect scroll events on the main element or window
// and fade all fadeable targets to 30% opacity while disabling pointer events.
export default class extends Controller {
  static targets = ["fadeable"];

  connect() {
    // Find the scroll container - prefer main element, fallback to window
    this.scrollContainer = document.querySelector("main") || window;

    // Track scrolling state to avoid redundant fade operations
    this.isScrolling = false;

    // Timeout for detecting when scrolling has stopped
    this.scrollTimeout = null;

    // Bind event handlers to maintain correct context
    this.handleScroll = this.handleScroll.bind(this);
    this.handleScrollEnd = this.handleScrollEnd.bind(this);

    // Add scroll listener with passive flag for better performance
    if (this.scrollContainer === window) {
      window.addEventListener("scroll", this.handleScroll, { passive: true });
    } else {
      this.scrollContainer.addEventListener("scroll", this.handleScroll, {
        passive: true,
      });
    }
  }

  disconnect() {
    // Clean up event listeners
    if (this.scrollContainer === window) {
      window.removeEventListener("scroll", this.handleScroll);
    } else {
      this.scrollContainer.removeEventListener("scroll", this.handleScroll);
    }

    // Clear any pending timeout
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
  }

  handleScroll() {
    // Only fade out once when scrolling starts
    if (!this.isScrolling) {
      this.isScrolling = true;
      this.fadeOut();
    }

    // Reset the timeout on each scroll event
    clearTimeout(this.scrollTimeout);

    // Wait 150ms after scrolling stops to fade back in
    this.scrollTimeout = setTimeout(() => this.handleScrollEnd(), 150);
  }

  handleScrollEnd() {
    this.isScrolling = false;
    this.fadeIn();
  }

  fadeOut() {
    // Fade all fadeable targets to 30% opacity and disable clicks
    this.fadeableTargets.forEach((target) => {
      target.style.transition = "opacity 0.3s ease-in-out";
      target.style.opacity = "0.3";
      target.style.pointerEvents = "none";
    });
  }

  fadeIn() {
    // Restore full opacity and re-enable clicks
    this.fadeableTargets.forEach((target) => {
      target.style.transition = "opacity 0.3s ease-in-out";
      target.style.opacity = "1";
      target.style.pointerEvents = "auto";
    });
  }
}
